/**
 * @file analyticsTracker.js
 * @description Motor de telemetria e métricas de acessos e navegação do Mapa Cultural.
 * Coleta dados 100% anonimizados (LGPD compliant) para painel gerencial e pesquisas acadêmicas.
 */

import { supabase } from './supabaseClient';

const LOCAL_STORAGE_VISITOR_KEY = 'map_visitor_id';
const LOCAL_STORAGE_BUFFER_KEY = 'map_analytics_local_buffer';
const SESSION_STORAGE_KEY = 'map_session_id';
const MAX_LOCAL_BUFFER_SIZE = 500;

// Referências diretas ao Supabase REST para sendBeacon (que não suporta headers via fetch)
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

/**
 * Envia payload de telemetria via navigator.sendBeacon — sobrevive a beforeunload/pagehide.
 * Fallback para saveToLocalBuffer quando sendBeacon não está disponível.
 */
const sendViaBeacon = (payload) => {
  try {
    if (!SUPABASE_URL || !SUPABASE_ANON_KEY) return false;
    if (typeof navigator === 'undefined' || !navigator.sendBeacon) return false;

    const url = `${SUPABASE_URL}/rest/v1/map_analytics`;

    // O Supabase REST API exige apikey e Authorization. Usamos a abordagem de fetch keepalive.
    // Fallback: tentamos fetch com keepalive: true (suportado por Chrome, Edge, Safari)
    if (typeof fetch === 'function') {
      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          'Prefer': 'return=minimal'
        },
        body: JSON.stringify(payload),
        keepalive: true
      }).catch(() => { /* silencioso — dados já estão no buffer local */ });
      return true;
    }

    return false;
  } catch {
    return false;
  }
};

// Gera identificador único aleatório (UUID v4 simplificado)
const generateUUID = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

// Obtém ou inicializa o Visitor ID persistente
export const getVisitorId = () => {
  try {
    let visitorId = localStorage.getItem(LOCAL_STORAGE_VISITOR_KEY);
    if (!visitorId) {
      visitorId = generateUUID();
      localStorage.setItem(LOCAL_STORAGE_VISITOR_KEY, visitorId);
    }
    return visitorId;
  } catch {
    return 'anonymous-visitor';
  }
};

// Obtém ou inicializa o Session ID volátil
export const getSessionId = () => {
  try {
    let sessionId = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!sessionId) {
      sessionId = generateUUID();
      sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
    }
    return sessionId;
  } catch {
    return 'anonymous-session';
  }
};

// Detecção simples e segura de dispositivo e ambiente
export const getClientMetadata = () => {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  let deviceType = 'desktop';
  if (/iPad|Tablet|PlayBook|Silk/i.test(ua)) {
    deviceType = 'tablet';
  } else if (/Mobi|Android|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)) {
    deviceType = 'mobile';
  }

  let os = 'Other';
  if (/Android/i.test(ua)) os = 'Android';
  else if (/iPhone|iPad|iPod/i.test(ua)) os = 'iOS';
  else if (/Windows/i.test(ua)) os = 'Windows';
  else if (/Macintosh|Mac OS X/i.test(ua)) os = 'macOS';
  else if (/Linux/i.test(ua)) os = 'Linux';

  let browser = 'Other';
  if (/Edg\//i.test(ua)) browser = 'Edge';
  else if (/OPR\//i.test(ua) || /Opera/i.test(ua)) browser = 'Opera';
  else if (/Chrome\//i.test(ua) && !/Edg\//i.test(ua)) browser = 'Chrome';
  else if (/Safari\//i.test(ua) && !/Chrome\//i.test(ua)) browser = 'Safari';
  else if (/Firefox\//i.test(ua)) browser = 'Firefox';

  let screenResolution = 'unknown';
  if (typeof window !== 'undefined' && window.screen) {
    screenResolution = `${window.screen.width}x${window.screen.height}`;
  }

  return {
    device_type: deviceType,
    os,
    browser,
    screen_resolution: screenResolution,
    referrer: typeof document !== 'undefined' ? document.referrer || null : null,
    path: typeof window !== 'undefined' ? window.location.pathname : '/'
  };
};

// Salva evento no buffer local para contingência e modo offline
const saveToLocalBuffer = (eventPayload) => {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_BUFFER_KEY);
    const list = raw ? JSON.parse(raw) : [];
    list.push(eventPayload);
    if (list.length > MAX_LOCAL_BUFFER_SIZE) {
      list.splice(0, list.length - MAX_LOCAL_BUFFER_SIZE);
    }
    localStorage.setItem(LOCAL_STORAGE_BUFFER_KEY, JSON.stringify(list));
  } catch (e) {
    // Ignora possíveis erros de quota de armazenamento
  }
};

// Circuit Breaker para suspender requisições remotas quando a tabela não existe no banco (PGRST205 / 404)
let isTableMissingCircuitBreaker = false;
let circuitBreakerExpiresAt = 0;

/**
 * Registra um evento analítico de telemetria no Supabase
 * @param {string} eventType Tipo do evento (ex: 'page_view', 'map_pan', 'event_view')
 * @param {string} eventCategory Categoria do evento ('navigation', 'content', 'filter', 'engagement')
 * @param {Object} eventData Metadados adicionais do evento
 */
export const trackEvent = async (eventType, eventCategory = 'navigation', eventData = {}) => {
  try {
    const metadata = getClientMetadata();
    const payload = {
      session_id: getSessionId(),
      visitor_id: getVisitorId(),
      event_type: eventType,
      event_category: eventCategory,
      event_data: eventData,
      device_type: metadata.device_type,
      browser: metadata.browser,
      os: metadata.os,
      screen_resolution: metadata.screen_resolution,
      referrer: metadata.referrer,
      path: metadata.path,
      created_at: new Date().toISOString()
    };

    // Sempre salva cópia no buffer local
    saveToLocalBuffer(payload);

    // Se o circuit breaker estiver ativo (tabela inexistente), não dispara requisições remotas desnecessárias
    if (isTableMissingCircuitBreaker && Date.now() < circuitBreakerExpiresAt) {
      return;
    }

    // Gravação assíncrona no Supabase sem bloquear a UI
    const { error } = await supabase.from('map_analytics').insert([payload]);
    if (error) {
      // Se a tabela não existir no Supabase (PGRST205 / 404), ativa o circuit breaker por 10 minutos
      if (error.code === 'PGRST205' || (error.message && error.message.includes('Could not find the table'))) {
        isTableMissingCircuitBreaker = true;
        circuitBreakerExpiresAt = Date.now() + 10 * 60 * 1000;
      }
      if (process.env.NODE_ENV === 'development') {
        console.debug('[Analytics] Log gravado localmente (Supabase:', error.message, ')');
      }
    } else {
      isTableMissingCircuitBreaker = false;
    }
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      console.debug('[Analytics] Telemetria em modo fallback local:', err);
    }
  }
};

// ============================================================================
// Funções Utilitárias Específicas de Telemetria com Debounce
// ============================================================================

let searchDebounceTimer = null;
const mapMoveDebounceTimers = new Map();

/**
 * Registra visualização de página inicial/carregamento do mapa
 */
export const trackPageView = (path = '/') => {
  return trackEvent('page_view', 'navigation', { path });
};

/**
 * Registra navegação espacial no mapa (Zoom, Panorâmica, etc.)
 * Cada tipo de ação possui seu próprio timer de debounce independente
 * para evitar que zoom descarte pan (e vice-versa).
 */
export const trackMapNavigation = (action, details = {}) => {
  if (mapMoveDebounceTimers.has(action)) {
    clearTimeout(mapMoveDebounceTimers.get(action));
  }
  mapMoveDebounceTimers.set(action, setTimeout(() => {
    trackEvent(`map_${action}`, 'navigation', details);
    mapMoveDebounceTimers.delete(action);
  }, 2000));
};

/**
 * Registra visualização de detalhes de um ponto cultural/evento
 */
export const trackEventView = (event) => {
  if (!event) return;
  return trackEvent('event_view', 'content', {
    event_id: event.id,
    event_title: event.nome || event.titulo || 'Sem título',
    municipio: event.municipio || (Array.isArray(event.municipios) ? event.municipios[0] : null) || 'Não informado',
    categoria: event.categoria || (Array.isArray(event.categorias) ? event.categorias[0] : null) || 'Geral'
  });
};

/**
 * Registra busca textual realizada pelo usuário
 */
export const trackSearch = (searchTerm, resultsCount = 0) => {
  if (!searchTerm || searchTerm.trim().length < 2) return;
  if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
  searchDebounceTimer = setTimeout(() => {
    trackEvent('search', 'filter', {
      search_term: searchTerm.trim(),
      results_count: resultsCount
    });
  }, 1200);
};

/**
 * Registra alteração de filtros (Município ou Categoria)
 */
export const trackFilterChange = (filterType, filterValue, resultsCount = 0) => {
  return trackEvent('filter_change', 'filter', {
    filter_type: filterType,
    filter_value: filterValue,
    results_count: resultsCount
  });
};

/**
 * Registra alteração da camada do mapa (Satélite vs Mapa Padrão)
 */
export const trackMapLayerChange = (layerType) => {
  return trackEvent('map_layer_change', 'navigation', { layer_type: layerType });
};

/**
 * Registra visualização ou expansão de mídia (foto/vídeo)
 */
export const trackMediaView = (mediaType, eventId, eventTitle, isLightbox = false) => {
  return trackEvent(isLightbox ? 'lightbox_open' : 'media_view', 'engagement', {
    media_type: mediaType,
    event_id: eventId,
    event_title: eventTitle
  });
};

/**
 * Registra clique em indicador direcionale (fora de tela)
 */
export const trackIndicatorClick = (eventId, eventTitle) => {
  return trackEvent('indicator_click', 'navigation', {
    event_id: eventId,
    event_title: eventTitle
  });
};

/**
 * Registra tempo de permanência e engajamento da sessão (Heartbeat).
 * @param {number} durationSeconds Segundos de permanência
 * @param {Object} options
 * @param {boolean} options.useBeacon Se true, usa fetch keepalive para sobreviver a beforeunload/pagehide
 */
export const trackDwellTime = (durationSeconds, { useBeacon = false } = {}) => {
  const metadata = getClientMetadata();
  const payload = {
    session_id: getSessionId(),
    visitor_id: getVisitorId(),
    event_type: 'duration_ping',
    event_category: 'engagement',
    event_data: { duration_seconds: durationSeconds },
    device_type: metadata.device_type,
    browser: metadata.browser,
    os: metadata.os,
    screen_resolution: metadata.screen_resolution,
    referrer: metadata.referrer,
    path: metadata.path,
    created_at: new Date().toISOString()
  };

  // Sempre salva no buffer local
  saveToLocalBuffer(payload);

  if (useBeacon) {
    // Usa fetch keepalive que sobrevive ao fechamento da aba
    sendViaBeacon(payload);
    return;
  }

  return trackEvent('duration_ping', 'engagement', { duration_seconds: durationSeconds });
};

/**
 * Tenta reenviar eventos do buffer local ao Supabase.
 * Chamado automaticamente quando a conectividade é restaurada (evento 'online').
 */
export const flushLocalBuffer = async () => {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_BUFFER_KEY);
    if (!raw) return;
    const list = JSON.parse(raw);
    if (!list || list.length === 0) return;

    // Limita a 50 eventos por flush para não sobrecarregar
    const batch = list.slice(0, 50);
    const { error } = await supabase.from('map_analytics').insert(batch);

    if (!error) {
      // Remove os itens enviados com sucesso do buffer
      const remaining = list.slice(batch.length);
      if (remaining.length > 0) {
        localStorage.setItem(LOCAL_STORAGE_BUFFER_KEY, JSON.stringify(remaining));
      } else {
        localStorage.removeItem(LOCAL_STORAGE_BUFFER_KEY);
      }
    }
  } catch {
    // Silencioso — próxima tentativa no próximo evento 'online'
  }
};

// Registra listener para sincronizar buffer quando a conexão for restaurada
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    flushLocalBuffer();
  }, { once: false });
}

/**
 * Exporta os métodos de controle de sessão
 */
export const analytics = {
  trackPageView,
  trackMapNavigation,
  trackEventView,
  trackSearch,
  trackFilterChange,
  trackMapLayerChange,
  trackMediaView,
  trackIndicatorClick,
  trackDwellTime,
  trackEvent,
  getVisitorId,
  getSessionId,
  flushLocalBuffer
};

export default analytics;
